import { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface IWindowSizeContextData {
  width: number;
  height: number;
}

interface IWindowSizeProvider {
  children: React.ReactNode;
}

const WindowSizeContext = createContext<IWindowSizeContextData>({} as IWindowSizeContextData);

const WindowSizeProvider: React.FC<IWindowSizeProvider> = ({ children }) => {
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const handleResize = (): void =>
      setWindowSize({
        width: window.innerWidth || 0,
        height: window.innerHeight || 0,
      });

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contextValue = useMemo(
    () => ({ width: windowSize.width, height: windowSize.height }),
    [windowSize.height, windowSize.width],
  );

  return <WindowSizeContext.Provider value={contextValue}>{children}</WindowSizeContext.Provider>;
};

const useWindowSize = (): IWindowSizeContextData => {
  const context = useContext(WindowSizeContext);
  if (!context) throw new Error('useWindowSize must be used within an WindowSizeProvider');
  return context;
};

export { WindowSizeProvider, useWindowSize };
